import React, { useEffect, useState } from 'react'
import { MailIcon, MessageSquareIcon } from 'lucide-react'
import { fetchMessageSharesForUser, fetchMessagesForShare } from '../../api/messageShares'
import type { MessageShare } from '../../types/inbox'

interface SharedMessagePacket {
  share: MessageShare
  messages: Array<{
    id: string
    body: string | null
    direction: string | null
    created_at: string
    contact_name: string | null
  }>
}

interface SharedMessagePacketsPanelProps {
  userId: string | null
  className?: string
}

export function SharedMessagePacketsPanel({ userId, className = '' }: SharedMessagePacketsPanelProps) {
  const [packets, setPackets] = useState<SharedMessagePacket[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!userId) return
    setLoading(true)
    fetchMessageSharesForUser(userId)
      .then(async (shares) => {
        const now = Date.now()
        const active = shares.filter((s) => !s.expires_at || new Date(s.expires_at).getTime() > now)
        const loaded = await Promise.all(
          active.map(async (share) => ({
            share,
            messages: await fetchMessagesForShare(share),
          }))
        )
        setPackets(loaded)
      })
      .catch(() => setPackets([]))
      .finally(() => setLoading(false))
  }, [userId])

  if (!userId || loading) return null
  if (packets.length === 0) return null

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${className}`}>
      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <MessageSquareIcon className="w-4 h-4 text-blue-600" />
        Shared messages
      </h3>
      <div className="space-y-4">
        {packets.map(({ share, messages }) => (
          <div key={share.id} className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-2">
            {share.note && <p className="text-xs text-gray-600 italic">{share.note}</p>}
            {messages.map((msg) => (
              <div key={msg.id} className="text-sm bg-white rounded-md border border-gray-100 px-3 py-2">
                <p className="text-[10px] text-gray-400 mb-1">
                  {msg.direction === 'inbound' ? 'Customer' : 'Office'} ·{' '}
                  {new Date(msg.created_at).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </p>
                <p className="text-gray-800 whitespace-pre-wrap">{msg.body || '(empty)'}</p>
              </div>
            ))}
            {share.expires_at && (
              <p className="text-[10px] text-gray-400 flex items-center gap-1">
                <MailIcon className="w-3 h-3" />
                Expires {new Date(share.expires_at).toLocaleDateString()}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}